/**
 * <hotel-customer-service> Web Component(声明式定制场景)
 *
 * 无 UI(不 attachShadow):纯副作用容器,气泡由 widget.js 挂 body。
 * 与自动挂载互斥:index.ts 自动挂载检测到本标签存在则跳过。
 *
 * 属性:
 *   tenant / chat-url / script-src(缺省由 fetchCustomerServiceConfig 经同源 BFF 自动解析)
 *   + 样式属性 color/position/icon/size/shape/variant/label/layout/offset-x/offset-y/precedence
 *
 * 样式属性变化需重挂 widget 才生效(widget.js 初始化时读 data-*)。
 */
import { fetchCustomerServiceConfig } from '../api/customer-service';
import { mountCustomerService } from './widget';
import type { CustomerServiceStyle, CustomerServiceHandle } from '../types';

const ATTR_TENANT = 'tenant';
const ATTR_CHAT_URL = 'chat-url';
const ATTR_SCRIPT_SRC = 'script-src';

/** 样式属性名 -> CustomerServiceStyle 字段 */
const STYLE_ATTRS: Record<string, keyof CustomerServiceStyle> = {
  color: 'color',
  position: 'position',
  icon: 'icon',
  size: 'size',
  shape: 'shape',
  variant: 'variant',
  label: 'label',
  layout: 'layout',
  'offset-x': 'offsetX',
  'offset-y': 'offsetY',
  precedence: 'precedence',
};

const NUMERIC_STYLE_KEYS: Array<keyof CustomerServiceStyle> = ['size', 'offsetX', 'offsetY'];

class HotelCustomerServiceElement extends HTMLElement {
  private handle: CustomerServiceHandle | null = null;
  private mounting = false;

  static get observedAttributes(): string[] {
    return [ATTR_TENANT, ATTR_CHAT_URL, ATTR_SCRIPT_SRC, ...Object.keys(STYLE_ATTRS)];
  }

  connectedCallback(): void {
    if (this.handle || this.mounting) return;
    void this.mount();
  }

  disconnectedCallback(): void {
    this.handle?.destroy();
    this.handle = null;
  }

  attributeChangedCallback(): void {
    // 首次属性设置可能在 connectedCallback 之前(handle 还没建),仅记录,连接时 mount 再用
    if (!this.handle || this.mounting) return;
    // 样式/配置变化需重挂才生效
    this.handle.destroy();
    this.handle = null;
    void this.mount();
  }

  /** 供命令式/外部主动刷新登录态(内部轮询已自动,此为兜底) */
  refresh(): void {
    this.handle?.refresh();
  }

  private async mount(): Promise<void> {
    this.mounting = true;
    const tenantId = this.getAttribute(ATTR_TENANT) || undefined;
    const chatUrl = this.getAttribute(ATTR_CHAT_URL) || undefined;
    const scriptSrc = this.getAttribute(ATTR_SCRIPT_SRC) || undefined;

    const style: CustomerServiceStyle = {};
    for (const [attr, key] of Object.entries(STYLE_ATTRS)) {
      const v = this.getAttribute(attr);
      if (v != null) {
        (style as Record<string, unknown>)[key] = NUMERIC_STYLE_KEYS.includes(key) ? Number(v) : v;
      }
    }

    let resolvedTenant = tenantId;
    let resolvedChatUrl = chatUrl;
    if (!resolvedTenant || !resolvedChatUrl) {
      const cs = await fetchCustomerServiceConfig();
      resolvedTenant = resolvedTenant || cs?.tenantId || undefined;
      resolvedChatUrl = resolvedChatUrl || cs?.chatUrl || undefined;
    }

    this.mounting = false;
    // disconnected 期间不再挂
    if (!this.isConnected) return;
    this.handle = mountCustomerService({
      tenantId: resolvedTenant,
      chatUrl: resolvedChatUrl,
      scriptSrc,
      style: Object.keys(style).length ? style : undefined,
    });
  }
}

/** 注册 <hotel-customer-service>(幂等)。由 index.ts 显式调用,避免依赖副作用 import 被 tree-shake。 */
export function registerCustomerService(): void {
  if (typeof customElements === 'undefined') return;
  if (customElements.get('hotel-customer-service')) return;
  customElements.define('hotel-customer-service', HotelCustomerServiceElement);
}

export { HotelCustomerServiceElement };
